import { createContext, useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase/config';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  signInWithPopup,
  getAdditionalUserInfo
} from 'firebase/auth';
import { getUserPreferences, cancelAccountDeletion } from '../firebase/services';
import i18n from '../i18n';
import WelcomeBackModal from '../components/common/WelcomeBackModal';

// Create the auth context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false); // Initialize loading to false
  const [isInitialLoad, setIsInitialLoad] = useState(true); // New state for initial auth check
  const [error, setError] = useState(null);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);


  // Sign up function
  const signup = async (email, password, { fullName }) => {
    setLoading(true); // Set loading to true during async operation
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update the user's profile with the display name
      if (fullName) {
        await updateProfile(userCredential.user, { displayName: fullName });
      }

      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false); // Set loading to false after async operation
    }
  };

  // Login function
  const login = async (email, password) => {
    setLoading(true); // Set loading to true during async operation
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false); // Set loading to false after async operation
    }
  };

  // Logout function
  const logout = async () => {
    setLoading(true); // Set loading to true during async operation
    try {
      setError(null);
      localStorage.removeItem('devUser');
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false); // Set loading to false after async operation
    }
  };

  // Password reset function
  const resetPassword = async (email) => {
    setLoading(true);
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-In function
  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false;
      return { user: result.user, isNewUser };
    } catch (error) {
      // Don't treat popup-closed as an error
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return null;
      }
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update user profile function
  const updateUserProfile = async (user, profileData) => {
    try {
      setError(null);
      await updateProfile(user, profileData);
      // Manually update the currentUser state to reflect changes immediately
      setCurrentUser(prevUser => ({ ...prevUser, ...profileData }));
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Check whether a freshly-authenticated user had a pending deletion request.
  // If so, restore their account immediately and show the WelcomeBackModal.
  const checkDeletionStatus = async (user) => {
    try {
      const prefs = await getUserPreferences(user.uid);
      if (!prefs.deletionRequested) return;

      const scheduledDate = prefs.deletionScheduledDate;
      const cutoff = scheduledDate
        ? (scheduledDate.toDate ? scheduledDate.toDate() : new Date(scheduledDate))
        : null;

      // Only restore if we're still within the 21-day grace window
      if (cutoff && cutoff <= new Date()) return;

      // Clear deletion flags in Firestore first so the account is immediately restored
      await cancelAccountDeletion(user.uid);
      setShowWelcomeBack(true);
    } catch (err) {
      console.error('Failed to check deletion status:', err);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsInitialLoad(false); // Initial load is complete

      if (user) {
        checkDeletionStatus(user);

        // Load saved language preference
        getUserPreferences(user.uid).then(prefs => {
          if (prefs?.language && prefs.language !== i18n.language) {
            i18n.changeLanguage(prefs.language);
          }
        }).catch(() => {
          // Silent fail — will use detected/default language
        });
      }
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Context value
  const value = {
    currentUser,
    loading,
    isInitialLoad, // Expose isInitialLoad
    error,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {isInitialLoad ? <div>Loading initial authentication...</div> : children}
      {showWelcomeBack && (
        <WelcomeBackModal onClose={() => setShowWelcomeBack(false)} />
      )}
    </AuthContext.Provider>
  );
};
